import globalConfig from '@utiles/officialStore/globalConfig';

/**
 * 将 webpack 打包后的资源路径转为完整 URL（供 AI 助手 SDK 使用）
 * @param {string} assetUrl
 * @returns {string}
 */
function toAbsoluteAssetUrl(assetUrl) {
  if (!assetUrl) {
    return '';
  }
  if (/^https?:\/\//i.test(assetUrl)) {
    return assetUrl;
  }
  if (typeof window === 'undefined') {
    return assetUrl;
  }
  try {
    return new URL(assetUrl, window.location.href).href;
  } catch (e) {
    return assetUrl;
  }
}

/**
 * entryTipStyle：入口引导气泡 + 主动防骚扰气泡共用
 * 消费方：
 * - EntryGuideTip / EntryWakeTip（entryTipRender=custom，props.tipStyle）
 * - CouponExpireTip / BrowseSilenceTip（props.tipStyle）
 *
 * 字段说明：
 * - background：SDK 入口 tip 整体背景色（兼容字段）
 * - headerBackground：主动气泡头部背景色（不传则回退 background）
 * - bodyBackground：主动气泡正文区背景色
 * - textColor：正文文案颜色
 * - highlightColor / nameColor：助手名称颜色（nameColor 优先）
 * - aiBadgeBackground：AI 标识背景色（不传则回退 gotItBtnBackground）
 * - border：弹窗边框，如 '1px solid #edceaa'
 * - borderRadius：弹窗圆角，如 '24px'
 * - assistantImage：头部圆形助手头像 URL
 * - minimizeBtnImage：右上角折叠按钮图 URL
 * - quietBtnBackground：左侧按钮背景色（无 quietBtnImage 时生效）
 * - quietBtnImage：左侧按钮背景图 URL（优先于 quietBtnBackground）
 * - quietBtnTextColor：左侧按钮文案颜色
 * - gotItBtnBackground：右侧按钮背景色（无 gotItBtnImage 时生效）
 * - gotItBtnImage：右侧按钮背景图 URL（优先于 gotItBtnBackground）
 * - gotItBtnTextColor：右侧按钮文案颜色
 */

/** 浅色主题默认 entryTipStyle（ro / light 复用） */
const LIGHT_ENTRY_TIP_STYLE = {
  // 弹窗外壳 / 分区背景
  background: '#eef7ff',
  headerBackground: '#66bdf9',
  bodyBackground: '#edf7ff',
  textColor: '#4a9de2',
  highlightColor: '#4081d8',
  nameColor: '#ffffff',
  aiBadgeBackground: '#4081d8',
  border: '1px solid #c8e5fe',
  borderRadius: '15px',
  // 形象 / 收起按钮
  assistantImage: '',
  minimizeBtnImage: toAbsoluteAssetUrl(
    require('@assets/image/officialTopupNew/payAssistant/light_fold_btn.png')
  ),
  // 左侧按钮（暂不打扰 / 暂时不需要）
  quietBtnBackground: '#9d9d9d',
  quietBtnImage: toAbsoluteAssetUrl(
    require('@assets/image/officialTopupNew/payAssistant/light_btn_cancel.png')
  ),
  quietBtnTextColor: '#ffffff',
  // 右侧按钮（我知道了 / 查看详情）
  gotItBtnBackground: '#6cb7ff',
  gotItBtnImage: toAbsoluteAssetUrl(
    require('@assets/image/officialTopupNew/payAssistant/light_btn_confirm.png')
  ),
  gotItBtnTextColor: '#ffffff',
};

/** 深色主题默认 entryTipStyle（对齐截图：深色头 + 浅色正文） */
const DARK_ENTRY_TIP_STYLE = {
  background: '#4c3d38',
  headerBackground: '#4c3d38',
  bodyBackground: '#f7f3ef',
  textColor: '#4a3f3a',
  highlightColor: '#edceaa',
  nameColor: '#edceaa',
  aiBadgeBackground: '#d08835',
  border: '1px solid rgba(237, 206, 170, 0.45)',
  borderRadius: '15px',
  assistantImage: '',
  minimizeBtnImage: toAbsoluteAssetUrl(
    require('@assets/image/officialTopupNew/payAssistant/dark_fold_btn.png')
  ),
  quietBtnBackground: '#8e8e8e',
  quietBtnImage: toAbsoluteAssetUrl(
    require('@assets/image/officialTopupNew/payAssistant/dark_btn_cancel.png')
  ),
  quietBtnTextColor: '#ffffff',
  gotItBtnBackground: '#d08835',
  gotItBtnImage: toAbsoluteAssetUrl(
    require('@assets/image/officialTopupNew/payAssistant/dark_btn_confirm.png')
  ),
  gotItBtnTextColor: '#ffffff',
};

/** 悬浮按钮位置（按横竖屏） */
export const FLOAT_POSITION = {
  landscape: { floatRight: 35, floatBottom: 120 },
  portrait: { floatRight: 10, floatBottom: 293 },
};

/**
 * 按横竖屏合并悬浮按钮位置到 theme
 * @param {Object} theme
 * @param {boolean} isLandscape
 * @returns {Object}
 */
export function applyFloatPosition(theme, isLandscape) {
  const pos = isLandscape
    ? FLOAT_POSITION.landscape
    : FLOAT_POSITION.portrait;
  return { ...theme, ...pos };
}

/** 对话窗尺寸与顶栏拖拽区（各游戏 theme 共用） */
const DIALOG_SHELL_THEME = {
  dialogSize: {
    width: 359,
    height: 502,
    expandedWidth: 623,
    expandedHeight: 757,
  },
  // 顶栏有两个按钮，drag-handle 右侧留白建议 92
  dialogDragHandle: { height: 76, rightInset: 92 },
};

/**
 * 合并对话窗 shell 主题（游戏可局部覆盖 dialogSize / dialogDragHandle）
 * @param {Object} theme
 * @returns {Object}
 */
export function applyDialogShellTheme(theme) {
  return {
    ...theme,
    dialogSize: {
      ...DIALOG_SHELL_THEME.dialogSize,
      ...(theme.dialogSize || {}),
    },
    dialogDragHandle: {
      ...DIALOG_SHELL_THEME.dialogDragHandle,
      ...(theme.dialogDragHandle || {}),
    },
  };
}

/** 悬浮图标主题预设 */
const THEME_PRESETS = {
  ro: {
    floatFullIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/ro_float.png')
    ),
    floatFullSize: { width: 82, height: 80 },
    floatHalfIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/ro_float_half.png')
    ),
    floatHalfSize: { width: 52, height: 80 },
    entryTipStyle: {
      ...LIGHT_ENTRY_TIP_STYLE,
      minimizeBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/ro_fold_btn.png')
      ),
      // 气泡头像优先 tip 专用图，无则回退悬浮图
      assistantImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/ro_tip_ico.png')
      ),
    },
  },
  dark: {
    floatFullIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/dark_float_ico.png')
    ),
    floatFullSize: { width: 80, height: 80 },
    floatHalfIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/dark_float_ico_half.png')
    ),
    floatHalfSize: { width: 52, height: 80 },
    floatHalfRight: -34,
    entryTipStyle: {
      ...DARK_ENTRY_TIP_STYLE,
      assistantImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/dark_tip_ico.png')
      ),
    },
  },
  light: {
    floatFullIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/light_float_ico.png')
    ),
    floatFullSize: { width: 80, height: 80 },
    floatHalfIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/light_float_ico_half.png')
    ),
    floatHalfSize: { width: 80, height: 80 },
    floatHalfRight: -34,
    entryTipStyle: {
      ...LIGHT_ENTRY_TIP_STYLE,
      assistantImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/light_tip_ico.png')
      ),
    },
  },
  pns: {
    floatFullIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/pns_float_ico.png')
    ),
    floatFullSize: { width: 82, height: 86 },
    floatHalfIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/pns_float_ico_half.png')
    ),
    floatHalfSize: { width: 45, height: 86 },
    entryTipStyle: {
      background: '#191617',
      headerBackground: '#393539',
      bodyBackground: '#191617',
      textColor: '#ffffff',
      highlightColor: '#edceaa',
      nameColor: '#edceaa',
      aiBadgeBackground: '#db9849',
      border: '2px solid rgba(132, 133, 135, 0.45)',
      borderRadius: '6px',
      quietBtnBackground: 'linear-gradient(180deg, #848484 0%, #848484 1%, #d9d9d9 100%)',
      quietBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/dark_btn_cancel.png')
      ),
      quietBtnTextColor: '#ffffff',
      gotItBtnBackground: 'linear-gradient(130deg, #917523 0%, #bba547 100%)',
      gotItBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/dark_btn_confirm.png')
      ),
      gotItBtnTextColor: '#ffffff',
      minimizeBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/pns_fold_btn.png')
      ),
      // 气泡头像优先 tip 专用图，无则回退悬浮图
      assistantImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/pns_tip_ico.png')
      ),
    },
  },
  k1: {
    floatFullIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/k1_float_ico.png')
    ),
    floatFullSize: { width: 80, height: 80 },
    floatHalfIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/k1_float_ico_half.png')
    ),
    floatHalfSize: { width: 80, height: 80 },
    entryTipStyle: {
      background: '#f2efea',
      headerBackground: '#3771c2',
      bodyBackground: '#f2efea',
      textColor: '#3771c2',
      highlightColor: '#f7a031',
      nameColor: '#ffffff',
      aiBadgeBackground: '#4e98fc',
      border: '2px solid rgba(165, 177, 185, 0.45)',
      borderRadius: '20px',
      quietBtnBackground: 'linear-gradient(180deg, #848484 0%, #848484 1%, #d9d9d9 100%)',
      quietBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/dark_btn_cancel.png')
      ),
      quietBtnTextColor: '#ffffff',
      gotItBtnBackground: 'linear-gradient(180deg, #6fabff 0%, #7bb8ff 100%)',
      gotItBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/dark_btn_confirm.png')
      ),
      gotItBtnTextColor: '#ffffff',
      minimizeBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/k1_fold_btn.png')
      ),
      // 气泡头像优先 tip 专用图，无则回退悬浮图
      assistantImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/k1_tip_ico.png')
      ),
    },
  },
  k3p: {
    floatFullIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/k3p_float_ico.png')
    ),
    floatFullSize: { width: 80, height: 80 },
    floatHalfIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/k3p_float_ico_half.png')
    ),
    floatHalfSize: { width: 80, height: 80 },
    entryTipStyle: {
      background: '#f9edd5',
      headerBackground: '#8c4a16',
      bodyBackground: '#f9edd5',
      textColor: '#7b6043',
      highlightColor: '#f7a031',
      nameColor: '#ffffff',
      aiBadgeBackground: '#bc5145',
      border: '2px solid rgba(219, 178, 152, 0.45)',
      borderRadius: '20px',
      quietBtnBackground: 'linear-gradient(180deg, #848484 0%, #848484 1%, #d9d9d9 100%)',
      quietBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/dark_btn_cancel.png')
      ),
      quietBtnTextColor: '#ffffff',
      gotItBtnBackground: 'linear-gradient(180deg, #ce7e01 0%, #ce7e01 1%, #f05e2b 99%, #f05e2b 100%)',
      gotItBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/dark_btn_confirm.png')
      ),
      gotItBtnTextColor: '#ffffff',
      minimizeBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/k3p_fold_btn.png')
      ),
      // 气泡头像优先 tip 专用图，无则回退悬浮图
      assistantImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/k3p_tip_ico.png')
      ),
    },
  },
  xdgslg: {
    floatFullIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/xdgslg_float_ico.png')
    ),
    floatFullSize: { width: 80, height: 80 },
    floatHalfIcon: toAbsoluteAssetUrl(
      require('@assets/image/officialTopupNew/payAssistant/xdgslg_float_ico_half.png')
    ),
    floatHalfSize: { width: 80, height: 80 },
    entryTipStyle: {
      background: '#f2efea',
      headerBackground: '#313552',
      bodyBackground: '#313553',
      textColor: '#ffffff',
      highlightColor: '#edceaa',
      nameColor: '#edceaa',
      aiBadgeBackground: '#b99b62',
      border: '2px solid rgba(52,51,65,0.4)',
      borderRadius: '18px',
      quietBtnBackground: 'linear-gradient(180deg, #848484 0%, #848484 1%, #d9d9d9 100%)',
      quietBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/dark_btn_cancel.png')
      ),
      quietBtnTextColor: '#ffffff',
      gotItBtnBackground: 'linear-gradient(145deg, #a57543 0%, #a57543 39%, #bfa66b 100%)',
      gotItBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/dark_btn_confirm.png')
      ),
      gotItBtnTextColor: '#ffffff',
      minimizeBtnImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/xdgslg_fold_btn.png')
      ),
      // 气泡头像优先 tip 专用图，无则回退悬浮图
      assistantImage: toAbsoluteAssetUrl(
        require('@assets/image/officialTopupNew/payAssistant/xdgslg_tip_ico.png')
      ),
    },
  }
};

/** 入口 / 主动气泡文案默认值（可按游戏在 GAME_CONFIG 局部覆盖） */
const DEFAULT_ENTRY_TEXTS = {
  'zh-CN': {
    assistantName: 'AI助手',
    // 入口引导
    gotItBtn: '我知道了',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: '暂时不需要',
    muteToast: '已隐藏，24小时內不再打扰',
    // BrowseSilenceTip
    browseSilenceMessage: '需要我帮忙吗？',
    browseSilenceMuteBtn: '不再提示',
    browseSilenceViewDetailBtn: '查看详情',
    // CouponExpireTip
    couponExpireMessage: '有张券马上过期，别浪费~',
    couponExpireViewDetailBtn: '去看看',
    // PaySuccessTip
    paySuccessMessage: '充值成功，已到账！\n去游戏里看看吧～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: '切换支付方式',
    payFailContactServiceBtn: '联系客服帮忙',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: '这张卡余额不太够，换一张试试？',
    payFailCardExpiredMessage: '这张卡过期啦，换一张常用的吧',
    payFailCardLostStolenMessage: '这张卡状态异常（可能挂失或被盗），先联系银行确认一下吧',
    payFailCardInvalidMessage: '卡号好像填错了，核对一下再试试吧',
    payFailRiskBlockMessage: '为了账户安全，这笔交易暂时被拦住了。提交工单我们帮你核实？',
    payFailIssuerTimeoutMessage: '银行那边有点忙，刷新一下再试试吧',
    payFailGeneralDeclineMessage: '暂时付不了，换种方式或找客服帮忙看看？',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: '部分商品无法加入购物车，请仔细确认后提交订单~',
  },
  'zh-TW': {
    // 入口引导
    gotItBtn: '我知道了',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: '暫時不需要',
    muteToast: '已隱藏，24小時內不再打擾',
    // BrowseSilenceTip
    browseSilenceMessage: '需要我幫忙嗎？',
    browseSilenceMuteBtn: '不再提示',
    browseSilenceViewDetailBtn: '查看詳情',
    // CouponExpireTip
    couponExpireMessage: '有張券馬上過期，別浪費~',
    couponExpireViewDetailBtn: '去看看',
    // PaySuccessTip
    paySuccessMessage: '儲值成功，已到帳！\n去遊戲裡看看吧～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: '切換支付方式',
    payFailContactServiceBtn: '聯繫客服幫忙',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: '這張卡餘額不太夠，換一張試試？',
    payFailCardExpiredMessage: '這張卡過期啦，換一張常用的吧',
    payFailCardLostStolenMessage: '這張卡狀態異常（可能掛失或被盜），先聯繫銀行確認一下吧',
    payFailCardInvalidMessage: '卡號好像填錯了，核對一下再試試吧',
    payFailRiskBlockMessage: '為了帳戶安全，這筆交易暫時被攔住了。提交工單我們幫你核實？',
    payFailIssuerTimeoutMessage: '銀行那邊有點忙，刷新一下再試試吧',
    payFailGeneralDeclineMessage: '暫時付不了，換種方式或找客服幫忙看看？',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: '部分商品無法加入購物車，請仔細確認後提交訂單~',
  },
  'en-US': {
    assistantName: 'AI Assistant',
    // 入口引导
    gotItBtn: 'Got it',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Not now',
    muteToast: 'Hidden – no more interruptions for 24h',
    // BrowseSilenceTip
    browseSilenceMessage: 'Need a hand?',
    browseSilenceMuteBtn: 'Don\'t show again',
    browseSilenceViewDetailBtn: 'View details',
    // CouponExpireTip
    couponExpireMessage: 'You have a coupon expiring soon, don\'t waste it~',
    couponExpireViewDetailBtn: 'Check it out',
    // PaySuccessTip
    paySuccessMessage: 'Recharge successful!\nCheck it in-game~',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Switch payment method',
    payFailContactServiceBtn: 'Contact support for help',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'Insufficient balance – try another card?',
    payFailCardExpiredMessage: 'This card has expired – use a different one',
    payFailCardLostStolenMessage: 'Card status abnormal – contact your bank to confirm',
    payFailCardInvalidMessage: 'Card number seems wrong – double‑check and try again',
    payFailRiskBlockMessage: 'For account security, this transaction is on hold. Submit a ticket for review?',
    payFailIssuerTimeoutMessage: 'Bank is busy – refresh and try again',
    payFailGeneralDeclineMessage: 'Can\'t pay right now – try another method or contact support',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Some items can\'t be added – please review and submit~',
  },
  'ja-JP': {
    // 入口引导
    gotItBtn: 'わかった',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: '今は不要',
    muteToast: '非表示にしました。24時間は通知しません',
    // BrowseSilenceTip
    browseSilenceMessage: 'お手伝いしましょうか？',
    browseSilenceMuteBtn: '表示しない',
    browseSilenceViewDetailBtn: '詳細を見る',
    // CouponExpireTip
    couponExpireMessage: 'クーポンがすぐに期限切れになります、無駄にしないで〜',
    couponExpireViewDetailBtn: '見に行く',
    // PaySuccessTip
    paySuccessMessage: 'チャージ成功！\nゲーム内で確認してね',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: '支払い方法を切り替える',
    payFailContactServiceBtn: 'カスタマーサポートに連絡',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: '残高が足りません。別のカードを試して',
    payFailCardExpiredMessage: 'このカードは期限切れです。別のカードを',
    payFailCardLostStolenMessage: 'このカードは異常です（紛失・盗難の可能性）— 銀行に確認を',
    payFailCardInvalidMessage: 'カード番号が間違っているようです。確認して再試行',
    payFailRiskBlockMessage: 'アカウント保護のため取引を保留しました。チケットを送信して確認を受けますか？',
    payFailIssuerTimeoutMessage: '銀行が混雑しています。更新して再試行',
    payFailGeneralDeclineMessage: '現在お支払いできません。別の方法かサポートへ',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: '一部商品がカートに追加できません。確認して注文を送信してください~',
  },
  'ko-KR': {
    // 入口引导
    gotItBtn: '알겠어요',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: '지금은 괜찮아요',
    muteToast: '숨겼어요, 24시간 동안 알림 없음',
    // BrowseSilenceTip
    browseSilenceMessage: '도움이 필요하신가요?',
    browseSilenceMuteBtn: '다시 표시 안 함',
    browseSilenceViewDetailBtn: '자세히 보기',
    // CouponExpireTip
    couponExpireMessage: '쿠폰이 곧 만료됩니다, 낭비하지 마세요~',
    couponExpireViewDetailBtn: '확인하러 가기',
    // PaySuccessTip
    paySuccessMessage: '충전 성공!\n게임에서 확인해 보세요~',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: '결제 방식 변경',
    payFailContactServiceBtn: '고객센터에 문의하세요',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: '잔액이 부족해요. 다른 카드를 시도해 보세요',
    payFailCardExpiredMessage: '카드가 만료됐어요. 다른 카드를 사용하세요',
    payFailCardLostStolenMessage: '카드 상태 이상(분실/도난 의심) – 은행에 확인하세요',
    payFailCardInvalidMessage: '카드 번호가 잘못된 것 같아요. 다시 확인하세요',
    payFailRiskBlockMessage: '계정 보호를 위해 거래가 보류됐어요. 티켓을 제출하시겠어요?',
    payFailIssuerTimeoutMessage: '은행이 바빠요. 새로고침 후 다시 시도',
    payFailGeneralDeclineMessage: '지금 결제할 수 없어요. 다른 방법이나 고객센터에 문의하세요',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: '일부 상품을 장바구니에 추가할 수 없어요. 확인 후 주문해 주세요~',
  },
  'th-TH': {
    // 入口引导
    gotItBtn: 'รู้แล้ว',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'ยังไม่ต้องการ',
    muteToast: 'ซ่อนแล้ว จะไม่รบกวน 24 ชม.',
    // BrowseSilenceTip
    browseSilenceMessage: 'ต้องการให้ช่วยไหม?',
    browseSilenceMuteBtn: 'ไม่แสดงอีก',
    browseSilenceViewDetailBtn: 'ดูรายละเอียด',
    // CouponExpireTip
    couponExpireMessage: 'มีคูปองจะหมดอายุเร็วๆ นี้ อย่าปล่อยให้เสีย~',
    couponExpireViewDetailBtn: 'ไปดูเลย',
    // PaySuccessTip
    paySuccessMessage: 'ชำระเงินสำเร็จ เข้าแล้ว!\nไปดูในเกมเลย～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'เปลี่ยนวิธีการชำระเงิน',
    payFailContactServiceBtn: 'ติดต่อฝ่ายช่วยเหลือ',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'ยอดเงินในบัตรนี้ไม่พอ ลองใช้ใบอื่นไหม?',
    payFailCardExpiredMessage: 'บัตรนี้หมดอายุแล้ว เปลี่ยนเป็นใบที่ใช้ประจำ',
    payFailCardLostStolenMessage: 'สถานะบัตรนี้ผิดปกติ (อาจสูญหายหรือถูกขโมย) กรุณาติดต่อธนาคารก่อน',
    payFailCardInvalidMessage: 'หมายเลขบัตรดูเหมือนกรอกผิด ตรวจสอบแล้วลองอีกครั้ง',
    payFailRiskBlockMessage: 'เพื่อความปลอดภัยของบัญชี รายการนี้ถูกระงับชั่วคราว ส่งตั๋วงานให้เราช่วยตรวจสอบไหม?',
    payFailIssuerTimeoutMessage: 'ทางธนาคารค่อนข้างยุ่ง ลองรีเฟรชแล้วลองอีกครั้ง',
    payFailGeneralDeclineMessage: 'ชำระเงินไม่ได้ชั่วคราว ลองเปลี่ยนวิธีหรือติดต่อฝ่ายช่วยเหลือดูไหม?',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'สินค้าบางรายการไม่สามารถเพิ่มลงตะกร้าได้ กรุณาตรวจสอบแล้วยืนยันคำสั่งซื้อ~',
  },
  'vi-VN': {
    // 入口引导
    gotItBtn: 'Rồi',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Tạm thời không cần',
    muteToast: 'Đã ẩn, sẽ không làm phiền trong 24h',
    // BrowseSilenceTip
    browseSilenceMessage: 'Cần tôi giúp không?',
    browseSilenceMuteBtn: 'Không hiển thị lại',
    browseSilenceViewDetailBtn: 'Xem chi tiết',
    // CouponExpireTip
    couponExpireMessage: 'Có một phiếu giảm giá sắp hết hạn, đừng lãng phí~',
    couponExpireViewDetailBtn: 'Đi xem ngay',
    // PaySuccessTip
    paySuccessMessage: 'Nạp thành công, đã vào tài khoản!\nVào game xem nhé～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Chuyển phương thức thanh toán',
    payFailContactServiceBtn: 'Liên hệ hỗ trợ',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'Số dư thẻ này không đủ, thử thẻ khác?',
    payFailCardExpiredMessage: 'Thẻ này đã hết hạn, đổi sang thẻ thường dùng nhé',
    payFailCardLostStolenMessage: 'Thẻ này có trạng thái bất thường (có thể bị mất hoặc đánh cắp), hãy liên hệ ngân hàng',
    payFailCardInvalidMessage: 'Số thẻ có vẻ sai, hãy kiểm tra lại và thử',
    payFailRiskBlockMessage: 'Vì an toàn tài khoản, giao dịch này tạm bị chặn. Gửi yêu cầu để chúng tôi xác minh?',
    payFailIssuerTimeoutMessage: 'Ngân hàng đang bận, hãy làm mới và thử lại',
    payFailGeneralDeclineMessage: 'Tạm thời không thanh toán được, đổi cách hoặc liên hệ hỗ trợ?',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Một số sản phẩm không thể thêm vào giỏ – vui lòng kiểm tra và gửi đơn hàng~',
  },
  'id-ID': {
    // 入口引导
    gotItBtn: 'Siap',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Belum perlu',
    muteToast: 'Disembunyikan – tidak akan mengganggu selama 24 jam',
    // BrowseSilenceTip
    browseSilenceMessage: 'Perlu bantuan?',
    browseSilenceMuteBtn: 'Jangan tampilkan lagi',
    browseSilenceViewDetailBtn: 'Lihat detail',
    // CouponExpireTip
    couponExpireMessage: 'Anda memiliki kupon yang akan segera kadaluarsa, jangan sia-siakan~',
    couponExpireViewDetailBtn: 'Lihat',
    // PaySuccessTip
    paySuccessMessage: 'Isi ulang berhasil! Sudah masuk!\nCek di game yuk～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Ganti metode pembayaran',
    payFailContactServiceBtn: 'Hubungi layanan pelanggan',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'Saldo kartu ini kurang, coba kartu lain?',
    payFailCardExpiredMessage: 'Kartu ini sudah kedaluwarsa, ganti dengan yang biasa',
    payFailCardLostStolenMessage: 'Status kartu tidak normal (mungkin hilang/dicuri), hubungi bank',
    payFailCardInvalidMessage: 'Nomor kartu sepertinya salah, periksa dan coba lagi',
    payFailRiskBlockMessage: 'Demi keamanan akun, transaksi ini ditahan sementara. Kirim tiket untuk kami verifikasi?',
    payFailIssuerTimeoutMessage: 'Bank sedang sibuk, segarkan dan coba lagi',
    payFailGeneralDeclineMessage: 'Untuk sementara tidak bisa bayar, coba cara lain atau hubungi dukungan?',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Beberapa barang tidak bisa ditambahkan – periksa dan kirim pesanan~',
  },
  'ru-RU': {
    // 入口引导
    gotItBtn: 'Понял',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Пока не нужно',
    muteToast: 'Скрыто, не будет беспокоить 24 часа',
    // BrowseSilenceTip
    browseSilenceMessage: 'Нужна помощь?',
    browseSilenceMuteBtn: 'Не показывать снова',
    browseSilenceViewDetailBtn: 'Подробнее',
    // CouponExpireTip
    couponExpireMessage: 'У вас есть купон, который скоро истекает, не тратьте зря~',
    couponExpireViewDetailBtn: 'Посмотреть',
    // PaySuccessTip
    paySuccessMessage: 'Пополнение успешно! Зачислено!\nЗайдите в игру～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Сменить способ оплаты',
    payFailContactServiceBtn: 'Связаться с поддержкой',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'На карте недостаточно средств, попробуйте другую',
    payFailCardExpiredMessage: 'Срок действия карты истёк, используйте другую',
    payFailCardLostStolenMessage: 'Статус карты аномальный (возможно, утеряна или украдена), свяжитесь с банком',
    payFailCardInvalidMessage: 'Номер карты введён неверно, проверьте и попробуйте снова',
    payFailRiskBlockMessage: 'В целях безопасности счёт, транзакция временно заблокирована. Отправить заявку на проверку?',
    payFailIssuerTimeoutMessage: 'Банк занят, обновите страницу и попробуйте снова',
    payFailGeneralDeclineMessage: 'Пока нельзя оплатить, попробуйте другой способ или обратитесь в поддержку',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Некоторые товары нельзя добавить – проверьте и подтвердите заказ~',
  },
  'de-DE': {
    // 入口引导
    gotItBtn: 'Verstanden',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Momentan nicht nötig',
    muteToast: 'Ausgeblendet – 24 Std. keine Störung',
    // BrowseSilenceTip
    browseSilenceMessage: 'Brauchen Sie Hilfe?',
    browseSilenceMuteBtn: 'Nicht mehr anzeigen',
    browseSilenceViewDetailBtn: 'Details anzeigen',
    // CouponExpireTip
    couponExpireMessage: 'Sie haben einen Gutschein, der bald abläuft, verschwenden Sie ihn nicht~',
    couponExpireViewDetailBtn: 'Anschauen',
    // PaySuccessTip
    paySuccessMessage: 'Aufladung erfolgreich! Gutgeschrieben!\nSchau im Spiel nach～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Zahlungsart wechseln',
    payFailContactServiceBtn: 'Support kontaktieren',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'Diese Karte hat zu wenig Guthaben, versuchen Sie eine andere',
    payFailCardExpiredMessage: 'Diese Karte ist abgelaufen, nehmen Sie eine andere',
    payFailCardLostStolenMessage: 'Kartenstatus ungewöhnlich – bitte kontaktieren Sie die Bank',
    payFailCardInvalidMessage: 'Kartennummer scheint falsch zu sein – überprüfen Sie es',
    payFailRiskBlockMessage: 'Aus Sicherheitsgründen wurde diese Transaktion vorübergehend gesperrt. Möchten Sie ein Ticket zur Prüfung einreichen?',
    payFailIssuerTimeoutMessage: 'Die Bank ist gerade beschäftigt – aktualisieren Sie und versuchen Sie es erneut',
    payFailGeneralDeclineMessage: 'Zahlung momentan nicht möglich – versuchen Sie eine andere Methode oder kontaktieren Sie den Support',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Einige Artikel können nicht hinzugefügt werden – bitte prüfen und bestellen~',
  },
  'fr-FR': {
    // 入口引导
    gotItBtn: 'Compris',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Pas pour l\'instant',
    muteToast: 'Masqué – plus de notification pendant 24h',
    // BrowseSilenceTip
    browseSilenceMessage: 'Besoin d\'aide ?',
    browseSilenceMuteBtn: 'Ne plus afficher',
    browseSilenceViewDetailBtn: 'Voir les détails',
    // CouponExpireTip
    couponExpireMessage: 'Vous avez un coupon qui expire bientôt, ne le gaspillez pas~',
    couponExpireViewDetailBtn: 'Voir',
    // PaySuccessTip
    paySuccessMessage: 'Recharge réussie ! Créditée !\nAllez voir dans le jeu～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Changer de moyen de paiement',
    payFailContactServiceBtn: 'Contacter le support',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'Cette carte a un solde insuffisant, essayez-en une autre',
    payFailCardExpiredMessage: 'Cette carte a expiré, utilisez-en une autre',
    payFailCardLostStolenMessage: 'Cette carte a un statut anormal – contactez votre banque',
    payFailCardInvalidMessage: 'Le numéro de carte semble incorrect – vérifiez et réessayez',
    payFailRiskBlockMessage: 'Pour la sécurité du compte, cette transaction est suspendue. Soumettre un ticket pour vérification ?',
    payFailIssuerTimeoutMessage: 'La banque est occupée – actualisez et réessayez',
    payFailGeneralDeclineMessage: 'Paiement temporairement impossible – essayez un autre moyen ou contactez le support',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Certains articles ne peuvent être ajoutés – vérifiez et validez~',
  },
  'es-ES': {
    // 入口引导
    gotItBtn: 'Vale',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Por ahora no',
    muteToast: 'Oculto – no molestaré durante 24h',
    // BrowseSilenceTip
    browseSilenceMessage: '¿Necesitas ayuda?',
    browseSilenceMuteBtn: 'No mostrar de nuevo',
    browseSilenceViewDetailBtn: 'Ver detalles',
    // CouponExpireTip
    couponExpireMessage: 'Tiene un cupón que caduca pronto, no lo desperdicie~',
    couponExpireViewDetailBtn: 'Ver',
    // PaySuccessTip
    paySuccessMessage: '¡Recarga exitosa! ¡Acreditado!\nVe al juego～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Cambiar método de pago',
    payFailContactServiceBtn: 'Contactar al servicio de atención',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'Esta tarjeta tiene saldo insuficiente, ¿pruebas otra?',
    payFailCardExpiredMessage: 'Esta tarjeta ha caducado, usa otra',
    payFailCardLostStolenMessage: 'Esta tarjeta tiene un estado anómalo – contacta con el banco',
    payFailCardInvalidMessage: 'El número de tarjeta parece incorrecto – verifica y vuelve a intentar',
    payFailRiskBlockMessage: 'Por seguridad, esta transacción ha sido retenida. ¿Enviamos un ticket para verificar?',
    payFailIssuerTimeoutMessage: 'El banco está ocupado – actualiza y vuelve a intentar',
    payFailGeneralDeclineMessage: 'De momento no se puede pagar – ¿pruebas otra forma o contactas con soporte?',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Algunos artículos no se pueden añadir – revisa y confirma el pedido~',
  },
  'pt-PT': {
    // 入口引导
    gotItBtn: 'Entendi',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Não por enquanto',
    muteToast: 'Oculto – não incomodarei por 24h',
    // BrowseSilenceTip
    browseSilenceMessage: 'Precisa de ajuda?',
    browseSilenceMuteBtn: 'Não mostrar novamente',
    browseSilenceViewDetailBtn: 'Ver detalhes',
    // CouponExpireTip
    couponExpireMessage: 'Você tem um cupom que expira em breve, não o desperdice~',
    couponExpireViewDetailBtn: 'Ver',
    // PaySuccessTip
    paySuccessMessage: 'Recarga bem-sucedida! Creditado!\nVeja no jogo～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Alterar método de pagamento',
    payFailContactServiceBtn: 'Contatar o suporte',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'Saldo insuficiente, tente outro cartão',
    payFailCardExpiredMessage: 'Este cartão expirou, use outro',
    payFailCardLostStolenMessage: 'Status do cartão anormal – contacte o banco',
    payFailCardInvalidMessage: 'Número do cartão parece errado – verifique e tente novamente',
    payFailRiskBlockMessage: 'Por segurança, esta transação foi retida. Enviar um ticket para verificarmos?',
    payFailIssuerTimeoutMessage: 'O banco está ocupado – atualize e tente novamente',
    payFailGeneralDeclineMessage: 'Temporariamente não é possível pagar – tente outro método ou contate o suporte?',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Alguns itens não podem ser adicionados – verifique e confirme o pedido~',
  },
  'tr-TR': {
    // 入口引导
    gotItBtn: 'Tamam',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Şimdilik gerek yok',
    muteToast: 'Gizlendi – 24 saat rahatsız etmeyecek',
    // BrowseSilenceTip
    browseSilenceMessage: 'Yardım ister misiniz?',
    browseSilenceMuteBtn: 'Tekrar gösterme',
    browseSilenceViewDetailBtn: 'Detayları gör',
    // CouponExpireTip
    couponExpireMessage: 'Yakında sona erecek bir kuponunuz var, boşa harcamayın~',
    couponExpireViewDetailBtn: 'Git bak',
    // PaySuccessTip
    paySuccessMessage: 'Yükleme başarılı! Hesaba yatırıldı!\nOyunda gör～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Ödeme yöntemini değiştir',
    payFailContactServiceBtn: 'Müşteri hizmetlerine başvur',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'Bu kartta bakiye yetersiz, başka bir kart dener misin?',
    payFailCardExpiredMessage: 'Bu kartın süresi doldu, başka bir tane kullan',
    payFailCardLostStolenMessage: 'Kart durumu anormal – bankayla iletişime geç',
    payFailCardInvalidMessage: 'Kart numarası yanlış görünüyor – kontrol edip tekrar dene',
    payFailRiskBlockMessage: 'Hesap güvenliği için bu işlem geçici olarak durduruldu. Onay için talep göndermek ister misiniz?',
    payFailIssuerTimeoutMessage: 'Banka meşgul – yenileyip tekrar dene',
    payFailGeneralDeclineMessage: 'Şu an ödeme yapılamıyor – başka bir yöntem dene veya desteğe başvur?',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Bazı ürünler sepete eklenemiyor – kontrol edip sipariş verin~',
  },
  'pl-PL': {
    // 入口引导
    gotItBtn: 'Rozumiem',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Na razie nie',
    muteToast: 'Ukryto – nie będę przeszkadzać przez 24h',
    // BrowseSilenceTip
    browseSilenceMessage: 'Potrzebujesz pomocy?',
    browseSilenceMuteBtn: 'Nie pokazuj ponownie',
    browseSilenceViewDetailBtn: 'Zobacz szczegóły',
    // CouponExpireTip
    couponExpireMessage: 'Masz kupon, który wkrótce wygaśnie, nie marnuj go~',
    couponExpireViewDetailBtn: 'Zobacz',
    // PaySuccessTip
    paySuccessMessage: 'Doładowanie udane! Zaksięgowane!\nSprawdź w grze～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Zmień metodę płatności',
    payFailContactServiceBtn: 'Skontaktuj się z pomocą',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'Na karcie za mało środków, spróbuj innej',
    payFailCardExpiredMessage: 'Ta karta wygasła, użyj innej',
    payFailCardLostStolenMessage: 'Stan karty nietypowy – skontaktuj się z bankiem',
    payFailCardInvalidMessage: 'Numer karty wydaje się błędny – sprawdź i spróbuj ponownie',
    payFailRiskBlockMessage: 'Dla bezpieczeństwa konta ta transakcja została wstrzymana. Czy wysłać zgłoszenie do weryfikacji?',
    payFailIssuerTimeoutMessage: 'Bank jest zajęty – odśwież i spróbuj ponownie',
    payFailGeneralDeclineMessage: 'Tymczasowa niemożność płatności – spróbuj innej metody lub skontaktuj się z pomocą?',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Niektóre przedmioty nie mogą zostać dodane – sprawdź i zamów~',
  },
  'it-IT': {
    // 入口引导
    gotItBtn: 'Capito',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'Per ora no',
    muteToast: 'Nascosto – non disturberò per 24h',
    // BrowseSilenceTip
    browseSilenceMessage: 'Hai bisogno di aiuto?',
    browseSilenceMuteBtn: 'Non mostrare più',
    browseSilenceViewDetailBtn: 'Visualizza dettagli',
    // CouponExpireTip
    couponExpireMessage: 'Hai un coupon che scade presto, non sprecarlo~',
    couponExpireViewDetailBtn: 'Vedi',
    // PaySuccessTip
    paySuccessMessage: 'Ricarica riuscita! Accreditato!\nVedi in gioco～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'Cambia metodo di pagamento',
    payFailContactServiceBtn: 'Contatta l\'assistenza',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'Saldo insufficiente, prova un\'altra carta',
    payFailCardExpiredMessage: 'Questa carta è scaduta, usane un\'altra',
    payFailCardLostStolenMessage: 'Stato carta anomalo – contatta la banca',
    payFailCardInvalidMessage: 'Il numero della carta sembra sbagliato – controlla e riprova',
    payFailRiskBlockMessage: 'Per sicurezza, questa transazione è stata bloccata. Vuoi inviare un ticket per la verifica?',
    payFailIssuerTimeoutMessage: 'La banca è occupata – aggiorna e riprova',
    payFailGeneralDeclineMessage: 'Al momento non è possibile pagare – prova un altro metodo o contatta l\'assistenza?',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'Alcuni articoli non possono essere aggiunti – controlla e invia l\'ordine~',
  },
  'ar-SA': {
    // 入口引导
    gotItBtn: 'فهمت',
    // 主动气泡共用（BrowseSilenceTip / CouponExpireTip）
    muteBtn: 'ليس الآن',
    muteToast: 'تم الإخفاء – لن أزعج لمدة 24 ساعة',
    // BrowseSilenceTip
    browseSilenceMessage: 'هل تحتاج مساعدة؟',
    browseSilenceMuteBtn: 'لا تظهر مرة أخرى',
    browseSilenceViewDetailBtn: 'عرض التفاصيل',
    // CouponExpireTip
    couponExpireMessage: 'لديك قسيمة تنتهي قريبًا، لا تضيعها~',
    couponExpireViewDetailBtn: 'اذهب للاطلاع',
    // PaySuccessTip
    paySuccessMessage: 'عملية الشحن ناجحة! تم الإيداع!\nاذهب إلى اللعبة～',
    // PayFailTip 共用按钮
    payFailSwitchPaytypeBtn: 'تغيير طريقة الدفع',
    payFailContactServiceBtn: 'اتصل بالدعم',
    // PayFailTip 各 failed_type 文案
    payFailBalanceErrorMessage: 'رصيد هذه البطاقة غير كافٍ، جرّب بطاقة أخرى؟',
    payFailCardExpiredMessage: 'انتهت صلاحية هذه البطاقة، استخدم بطاقة أخرى',
    payFailCardLostStolenMessage: 'حالة البطاقة غير طبيعية – اتصل بالبنك',
    payFailCardInvalidMessage: 'رقم البطاقة يبدو خاطئاً – تحقق وحاول مرة أخرى',
    payFailRiskBlockMessage: 'لأمان الحساب، تم تعليق هذه المعاملة مؤقتاً. هل ترغب في إرسال تذكرة للتحقق؟',
    payFailIssuerTimeoutMessage: 'البنك مشغول – قم بالتحديث وحاول مرة أخرى',
    payFailGeneralDeclineMessage: 'لا يمكن الدفع حالياً – جرب طريقة أخرى أو اتصل بالدعم؟',
    // 购物车失败切支付方式：部分商品未能加购
    payFailCartSkippedTip: 'لا يمكن إضافة بعض العناصر – تحقق وأرسل الطلب~',
  },
};

/** 默认配置（未开启游戏） */
const DEFAULT_CONFIG = {
  enable: false,
  useDrag: true,
  theme: THEME_PRESETS.light,
  useHalfFloatDrag: false,
  entryTexts: DEFAULT_ENTRY_TEXTS,
  /**
   * 是否在打开对话前调用宿主 onBeforeShow（如登录校验）
   * true：点击助手 / show() 前先回调，返回 true 才弹窗
   * false：不回调，直接弹窗
   */
  useBeforeShow: true,
};

/**
 * 按游戏覆盖的支付助手配置
 * - enable: 是否开启
 * - themeKey: 使用 THEME_PRESETS 中的预设；也可直接传 theme 对象覆盖
 * - entryTexts: 多语言文案（入口 + 主动气泡 + 对话窗助手名），与 DEFAULT_ENTRY_TEXTS 深合并
 *   assistantName 为唯一业务配置源：SDK 外壳气泡与 Chat App 对话窗均读取此字段
 *   只需写差异字段；未写的语言 / 字段沿用默认（仅对该字段生效）
 *   muteBtn / muteToast 为 CouponExpireTip 等共用；BrowseSilenceTip 使用 browseSilenceMuteBtn / browseSilenceViewDetailBtn
 *   payFail* 为 PayFailTip 文案 / 按钮（结构见 payFailTip.js）
 *   消费方不写硬编码兜底文案：配置没有则不展示（语言回退 en-US 配置）
 * - useBeforeShow: 是否调用宿主 onBeforeShow（登录等守卫），默认 true
 * - floatZIndex / tipZIndex / dialogZIndex: 悬浮按钮 / 气泡 / 对话壳层级
 *   默认分别为 99999 / 100000 / 99999；可写在 GAME_CONFIG 顶层或 theme 内
 * - 其余字段会与 DEFAULT_CONFIG 合并
 *
 * @example
 * 191: {
 *   enable: true,
 *   themeKey: 'dark',
 *   entryTexts: {
 *     'zh-CN': { assistantName: '小狐狸' },
 *   },
 *   useBeforeShow: true,
 *   floatZIndex: 10050,
 *   tipZIndex: 10051,
 *   dialogZIndex: 10060,
 * }
 */
/** 小助手名称多语言 key */
const ASSISTANT_NAME_LANGS = [
  'zh-CN',
  'zh-TW',
  'en-US',
  'ja-JP',
  'ko-KR',
  'th-TH',
  'vi-VN',
  'ru-RU',
  'de-DE',
  'fr-FR',
  'id-ID',
  'es-ES',
  'pt-PT',
  'tr-TR',
  'pl-PL',
  'it-IT',
  'ar-SA',
];

/**
 * 按语言映射构建 entryTexts（仅 assistantName）
 * @param {Record<string, string>} nameByLang
 * @returns {Object}
 */
function buildAssistantNameTexts(nameByLang = {}) {
  const entryTexts = {};
  ASSISTANT_NAME_LANGS.forEach((lang) => {
    const assistantName = nameByLang[lang];
    if (assistantName) {
      entryTexts[lang] = { assistantName };
    }
  });
  return entryTexts;
}

/** 各游戏小助手名称（多语言） */
const GAME_ASSISTANT_NAMES = {
  386: {
    'zh-CN': '小波利',
    'zh-TW': '小波利',
    'en-US': 'Poring',
    'ja-JP': 'Poring',
    'ko-KR': 'Poring',
    'th-TH': 'Poring',
    'vi-VN': 'Poring',
    'ru-RU': 'Poring',
    'de-DE': 'Poring',
    'fr-FR': 'Poring',
    'id-ID': 'Poring',
    'es-ES': 'Poring',
    'pt-PT': 'Poring',
    'tr-TR': 'Poring',
    'pl-PL': 'Poring',
    'it-IT': 'Poring',
    'ar-SA': 'Poring',
  },
  191: {
    'zh-CN': '诺娃',
    'zh-TW': '諾娃',
    'en-US': 'Nova',
    'ja-JP': 'ノヴァ',
    'ko-KR': '노바',
    'th-TH': 'Nova',
    'vi-VN': 'Nova',
    'ru-RU': 'Нова',
    'de-DE': 'Nova',
    'fr-FR': 'Nova',
    'id-ID': 'Nova',
    'es-ES': 'Nova',
    'pt-PT': 'Nova',
    'tr-TR': 'Nova',
    'pl-PL': 'Nova',
    'it-IT': 'Nova',
    'ar-SA': 'نوفا',
  },
  285: {
    'zh-CN': '卢克',
    'zh-TW': '盧克',
    'en-US': 'Luke',
    'ja-JP': 'ルック',
    'ko-KR': '루크',
    'th-TH': 'Luke',
    'vi-VN': 'Luke',
    'ru-RU': 'Люк',
    'de-DE': 'Luke',
    'fr-FR': 'Luke',
    'id-ID': 'Luke',
    'es-ES': 'Luke',
    'pt-PT': 'Luke',
    'tr-TR': 'Luke',
    'pl-PL': 'Luke',
    'it-IT': 'Luke',
    'ar-SA': 'Luke',
  },
  454: {
    'zh-CN': '诺拉',
    'zh-TW': '諾拉',
    'en-US': 'Nora',
    'ja-JP': 'Nora',
    'ko-KR': 'Nora',
    'th-TH': 'นอร่า',
    'vi-VN': 'Nora',
    'ru-RU': 'Нора',
    'de-DE': 'Nora',
    'fr-FR': 'Nora',
    'id-ID': 'Nora',
    'es-ES': 'Nora',
    'pt-PT': 'Nora',
    'tr-TR': 'Nora',
    'pl-PL': 'Nora',
    'it-IT': 'Nora',
    'ar-SA': 'نورا',
  },
  440: {
    'zh-CN': '瘟疫医生',
    'zh-TW': '瘟疫醫生',
    'en-US': 'Plague Doctor',
    'ja-JP': 'ペストドクター',
    'ko-KR': '역병 의사',
    'th-TH': 'หมอโรคระบาด',
    'vi-VN': 'Bác Sĩ Dịch Hạch',
    'ru-RU': 'Чумной Доктор',
    'de-DE': 'Seuchenarzt',
    'fr-FR': 'Docteur de la Peste',
    'id-ID': 'Dokter Wabah',
    'es-ES': 'Doctor de la peste',
    'pt-PT': 'Médico da Peste',
    'tr-TR': 'Veba Doktoru',
    'pl-PL': 'Doktor plagi',
    'it-IT': 'Dottore della Peste',
    'ar-SA': 'Plague Doctor',
  },
};

const DEFAULT_GAME_ASSISTANT_OPTIONS = {
  enable: true,
  useHalfFloatDrag: false,
  floatZIndex: 99,
  tipZIndex: 99,
  dialogZIndex: 99,
  useBeforeShow: true,
};

const GAME_CONFIG = {
  191: {
    ...DEFAULT_GAME_ASSISTANT_OPTIONS,
    enable: false,
    themeKey: 'pns',
    entryTexts: buildAssistantNameTexts(GAME_ASSISTANT_NAMES[191]),
  },
  386: {
    ...DEFAULT_GAME_ASSISTANT_OPTIONS,
    themeKey: 'ro',
    entryTexts: buildAssistantNameTexts(GAME_ASSISTANT_NAMES[386]),
  },
  285: {
    ...DEFAULT_GAME_ASSISTANT_OPTIONS,
    enable: false,
    themeKey: 'k1',
    entryTexts: buildAssistantNameTexts(GAME_ASSISTANT_NAMES[285]),
  },
  454: {
    ...DEFAULT_GAME_ASSISTANT_OPTIONS,
    enable: false,
    themeKey: 'k3p',
    entryTexts: buildAssistantNameTexts(GAME_ASSISTANT_NAMES[454]),
  },
  440: {
    ...DEFAULT_GAME_ASSISTANT_OPTIONS,
    enable: false,
    themeKey: 'xdgslg',
    entryTexts: buildAssistantNameTexts(GAME_ASSISTANT_NAMES[440]),
  },
};

/**
 * 从游戏配置抽出层级覆盖并合并进 theme（顶层字段优先于 theme 内同名字段）
 * @param {Object} theme
 * @param {{ floatZIndex?: number, tipZIndex?: number, dialogZIndex?: number }} zIndexOverride
 * @returns {Object}
 */
function mergeThemeZIndex(theme, zIndexOverride = {}) {
  const next = { ...theme };
  ['floatZIndex', 'tipZIndex', 'dialogZIndex'].forEach((key) => {
    const val = zIndexOverride[key];
    if (val != null && Number.isFinite(Number(val))) {
      next[key] = Math.trunc(Number(val));
    }
  });
  return next;
}

/**
 * 多语言入口文案深合并：游戏有配置的语言/字段覆盖默认，其余沿用默认
 * 仅对 entryTexts 生效；新增语言也会保留（以 override 为准）
 * @param {Object} defaults
 * @param {Object} [override]
 * @returns {Object}
 */
function mergeEntryTexts(defaults = {}, override) {
  if (!override || typeof override !== 'object') {
    return { ...defaults };
  }
  const langKeys = new Set([
    ...Object.keys(defaults),
    ...Object.keys(override),
  ]);
  const merged = {};
  langKeys.forEach((lang) => {
    const base = defaults[lang];
    const patch = override[lang];
    if (base && patch && typeof patch === 'object') {
      merged[lang] = { ...base, ...patch };
    } else if (patch && typeof patch === 'object') {
      merged[lang] = { ...patch };
    } else if (base) {
      merged[lang] = { ...base };
    }
  });
  return merged;
}

/**
 * 获取指定游戏的支付助手配置
 * enable=false 或未配置该游戏时，只返回 { enable: false }，不组装助手初始化参数
 * @param {string|number} gameId
 * @param {{ isLandscape?: boolean }} [options]
 * @returns {{ enable: false } | Object}
 */
export function getPayAssistantConfig(gameId, { isLandscape = false } = {}) {
  const id = +gameId || 0;
  const gameOverride = GAME_CONFIG[id];

  // 未配置 / 未开启：不初始化助手
  if (!gameOverride || !gameOverride.enable) {
    return { enable: false };
  }

  const {
    themeKey,
    theme: themeOverride,
    floatZIndex,
    tipZIndex,
    dialogZIndex,
    entryTexts: entryTextsOverride,
    ...restOverride
  } = gameOverride;

  let theme = DEFAULT_CONFIG.theme;
  if (themeOverride) {
    theme = themeOverride;
  } else if (themeKey && THEME_PRESETS[themeKey]) {
    theme = THEME_PRESETS[themeKey];
  }

  theme = mergeThemeZIndex(theme, { floatZIndex, tipZIndex, dialogZIndex });
  theme = applyFloatPosition(theme, isLandscape);
  theme = applyDialogShellTheme(theme);

  return {
    ...DEFAULT_CONFIG,
    ...restOverride,
    enable: true,
    gameId: id || '',
    chatAppUrl: `https://gabres.${globalConfig.getMainHost()}/aiGameBuddy/chat-app-pay-assistant/index.html`,
    theme,
    entryTexts: mergeEntryTexts(DEFAULT_ENTRY_TEXTS, entryTextsOverride),
    // 横屏禁用悬浮球拖拽，竖屏启用
    useFloatDrag: !isLandscape,
  };
}
